import { useId, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AiOperationDto, AiOperationType } from '@scoutaround/script-breakdown-shared';

import { api, ApiError } from '../../lib/api.js';
import { useCan, useRefusal } from '../../lib/permissions.js';
import { newRequestKey } from '../conversations/hooks.js';
import { Callout } from '../Callout.js';
import { CreditPackagePicker } from './CreditPackagePicker.js';
import { creditAmount, creditRange } from './format.js';
import { trackAiOperation } from './sessionMemory.js';
import {
  aiBillingMode,
  answerNeverArrived,
  insufficientAmounts,
  refreshCredits,
  refusalCode,
  useProjectCredits,
} from './useProjectCredits.js';

import '../../styles/credits.css';

/**
 * The refusals a start can get when it was approved already — in another tab, or by this press
 * whose answer never arrived (the retry then finds the run it queued, `EXTRACTION_ALREADY_RUNNING`).
 * On either, the panel reads the operation before saying anything.
 */
const ALREADY_STARTED_CODES: ReadonlySet<string> = new Set(['OPERATION_STATE_CONFLICT', 'EXTRACTION_ALREADY_RUNNING']);

/**
 * Where an operation stands once it was approved: past approval, with its maximum reserved,
 * running, or already settled. A start refused on an operation in one of these is a start that
 * happened, not a failure.
 */
const APPROVED_STATUSES: ReadonlySet<string> = new Set([
  'RESERVED',
  'RUNNING',
  'SETTLEMENT_PENDING',
  'RECONCILIATION_REQUIRED',
  'SETTLED',
]);

/** Every state the panel can be in; `data-state` on its root says which, for QA. */
export type ApprovalState =
  | 'loading'
  | 'estimate-error'
  | 'ready'
  | 'starting'
  | 'insufficient'
  | 'expired'
  | 'stale'
  | 'failed'
  | 'started'
  | 'cancelled'
  | 'paused';

/**
 * WHAT AN AI OPERATION WILL COST, AND NOTHING RUNS UNTIL SOMEBODY SAYS YES
 * (S6, S5; design B.5).
 *
 * Mounting it asks for an estimate — free: no AI request is made to estimate —
 * and shows the server's figures as it sent them:
 *
 *   - "Estimated cost: €0.35–€0.85", the minimum floored and the maximum ceiled;
 *   - "Available project credits", the balance floored;
 *   - "Maximum authorised cost", which is EXACTLY the maximum shown, and exactly
 *     what Start reserves (P6). Nothing above it can ever be charged.
 *
 * Start approves THIS quote with an `Idempotency-Key` minted for that press,
 * re-sent only when the same press is retried after an answer that never came.
 * Cancel releases it, and nothing is charged.
 *
 * NOT ENOUGH CREDITS IS AN OFFER, NEVER AN ALARM (B.7): toned warn, with the
 * available and the needed figures, "Buy credits" in place for a member who may
 * buy, and "a producer can add credits" for one who may not. The quote and the
 * uploaded screenplay are kept across the purchase; afterwards Start approves
 * the SAME quote while it is valid, and an expired or out-of-date one is
 * re-estimated from the same inputs in one click.
 *
 * Never shown, here or anywhere a producer looks: provider cost, per-token
 * price, markup, margin, FX. The answers the panel reads cannot carry them
 * (`lib/api.ts`, CREDIT_ANSWERS), so there is nothing to forget to hide.
 */
export function AiCostApproval({
  projectId,
  operationType,
  scriptVersionId,
  sceneIds,
  continuesOperationId,
  onStarted,
  onClose,
  testId = 'ai-approval',
}: {
  projectId: string;
  operationType: AiOperationType;
  scriptVersionId: string;
  /** A selection of scenes; absent is the whole screenplay. */
  sceneIds?: readonly string[];
  /** The stopped-at-cap operation this one continues. */
  continuesOperationId?: string;
  /** Once the operation is approved and its maximum reserved. */
  onStarted?: (operation: AiOperationDto) => void;
  /** Close the panel after a cancel or a pause. Absent: the panel stays, saying so. */
  onClose?: () => void;
  testId?: string;
}) {
  const queryClient = useQueryClient();
  const headingId = useId();
  const credits = useProjectCredits(projectId);
  const mayBuy = useCan('BILLING_PURCHASE');
  const runRefusal = useRefusal('AI_RUN');

  const [attempt, setAttempt] = useState(0);
  const [buying, setBuying] = useState(false);
  const [toppedUp, setToppedUp] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [purchaseRefused, setPurchaseRefused] = useState(false);
  /** The key of the press of Start being answered; a retry of that press re-sends it. */
  const approveKey = useRef<string | null>(null);

  const estimate = useQuery({
    queryKey: [
      'ai-estimate',
      projectId,
      operationType,
      scriptVersionId,
      sceneIds === undefined ? null : sceneIds.join(','),
      continuesOperationId ?? null,
      attempt,
    ],
    queryFn: () =>
      api.estimateAiOperation(projectId, { operationType, scriptVersionId, sceneIds, continuesOperationId }),
    // An estimate writes an operation row, so it is asked for once per opening
    // (and once per "Estimate again"), never behind the producer's back.
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    gcTime: 0,
  });

  const approve = useMutation({
    mutationFn: async (input: { operationId: string; key: string }) => {
      try {
        return await api.approveAiOperation(projectId, input.operationId, input.key, {
          operationType,
          scriptVersionId,
          sceneIds,
          continuesOperationId,
        });
      } catch (error) {
        const code = refusalCode(error);
        if (code === null || !ALREADY_STARTED_CODES.has(code)) throw error;
        // Approved already: read where it stands, and follow it if it started.
        const current = await api.aiOperation(projectId, input.operationId).catch(() => null);
        if (current !== null && APPROVED_STATUSES.has(current.status)) return current;
        throw error;
      }
    },
    onSuccess: async (operation) => {
      trackAiOperation(projectId, { operationId: operation.operationId, scriptVersionId });
      // The reservation has moved the balance.
      await refreshCredits(queryClient, projectId);
      onStarted?.(operation);
    },
  });

  const cancel = useMutation({
    mutationFn: (operationId: string) => api.cancelAiOperation(projectId, operationId),
    // An unapproved estimate reserves nothing and lapses on its own, so a
    // cancel that did not reach the server leaves nothing to undo.
    onSettled: () => {
      setCancelled(true);
    },
  });

  const start = (retryingSamePress: boolean) => {
    const operationId = estimate.data?.operationId;
    if (operationId === undefined) return;
    const key = retryingSamePress && approveKey.current !== null ? approveKey.current : newRequestKey();
    approveKey.current = key;
    approve.mutate({ operationId, key });
  };

  const estimateAgain = () => {
    approve.reset();
    cancel.reset();
    setBuying(false);
    setToppedUp(false);
    approveKey.current = null;
    setAttempt((n) => n + 1);
  };

  const cancelNow = () => {
    const operationId = estimate.data?.operationId;
    if (operationId === undefined) {
      setCancelled(true);
      return;
    }
    cancel.mutate(operationId);
  };

  const paused =
    aiBillingMode(credits.data) === 'SUSPENDED' ||
    refusalCode(estimate.error) === 'AI_BILLING_UNAVAILABLE' ||
    refusalCode(approve.error) === 'AI_BILLING_UNAVAILABLE';

  const approveCode = refusalCode(approve.error);
  const quote = estimate.data;

  // An approved operation stays "started" whatever happens next: a pause stops
  // new work, never one that is already running.
  const state: ApprovalState = approve.isSuccess
    ? 'started'
    : cancelled
      ? 'cancelled'
      : paused
        ? 'paused'
        : estimate.isPending
          ? 'loading'
          : estimate.isError || quote === undefined
            ? 'estimate-error'
            : approve.isPending
              ? 'starting'
              : approve.isError
                ? approveCode === 'CREDITS_INSUFFICIENT'
                  ? 'insufficient'
                  : approveCode === 'QUOTE_EXPIRED'
                    ? 'expired'
                    : approveCode === 'QUOTE_STALE_INPUT'
                      ? 'stale'
                      : 'failed'
                : !quote.sufficient && !toppedUp
                  ? 'insufficient'
                  : 'ready';

  const canPurchase = mayBuy && !purchaseRefused;

  const frame = (body: ReactNode, actions?: ReactNode) => (
    <section className="credits-approval" data-testid={testId} data-state={state} aria-labelledby={headingId}>
      <p className="colh" id={headingId} style={{ margin: 0 }} data-testid="ai-approval-title">
        {quote?.title ?? 'AI operation'}
      </p>
      {body}
      {actions === undefined ? null : <div className="credits-actions">{actions}</div>}
    </section>
  );

  const closeButton =
    onClose === undefined ? undefined : (
      <button type="button" className="ctl" data-testid="ai-approval-close" onClick={onClose}>
        Close
      </button>
    );

  const cancelButton = (
    <button
      type="button"
      className="ctl"
      data-testid="ai-approval-cancel"
      disabled={approve.isPending || cancel.isPending}
      onClick={cancelNow}
    >
      {cancel.isPending ? 'Cancelling…' : 'Cancel'}
    </button>
  );

  const estimateAgainButton = (
    <button type="button" className="btn btn-primary" data-testid="ai-approval-reestimate" onClick={estimateAgain}>
      Estimate again
    </button>
  );

  if (state === 'paused') {
    return frame(
      <Callout tone="warn" title="AI analysis is paused" testId="ai-approval-paused">
        New AI work cannot be started right now. Anything already running finishes and is charged as
        usual, and the credits on this production are safe.
      </Callout>,
      closeButton,
    );
  }

  if (state === 'cancelled') {
    return frame(
      <Callout tone="info" title="Cancelled" testId="ai-approval-cancelled">
        Nothing was reserved or charged.
      </Callout>,
      closeButton,
    );
  }

  if (state === 'started') {
    return frame(
      <Callout tone="ok" title="Started" testId="ai-approval-started">
        It runs in the background, and you can leave this page. When it finishes, what it cost is shown
        at the top of this production’s pages and in the credit history on Plan &amp; billing.
      </Callout>,
    );
  }

  if (state === 'loading') {
    return frame(
      <p className="mm credits-note" data-testid="ai-approval-loading" role="status" aria-live="polite">
        Working out what this will cost…
      </p>,
      cancelButton,
    );
  }

  if (state === 'estimate-error' || quote === undefined) {
    const refusal = estimateRefusal(estimate.error, runRefusal);

    return frame(
      <Callout tone={refusal.tone} title={refusal.title} testId="ai-approval-estimate-error">
        <span data-code={refusalCode(estimate.error) ?? ''}>{refusal.body}</span>
      </Callout>,
      <>
        {closeButton ?? cancelButton}
        {refusal.retry ? (
          <button type="button" className="btn btn-primary" data-testid="ai-approval-estimate-retry" onClick={estimateAgain}>
            Try again
          </button>
        ) : null}
      </>,
    );
  }

  const range = (
    <p className="credits-line" data-testid="ai-approval-range">
      Estimated cost: <strong>{creditRange(quote.minCents, quote.maxCents)}</strong>
    </p>
  );

  if (state === 'insufficient') {
    const refused = insufficientAmounts(approve.error);
    // The refusal's figures are the freshest; without one, the quote's own.
    const available = refused.availableCents ?? quote.availableCents;
    const required = refused.requiredCents ?? quote.capCents;

    return frame(
      <>
        <div className="credits-offer">
          <Callout tone="warn" title="More credits are needed to start this" testId="ai-approval-insufficient">
            <p className="credits-line" data-testid="ai-approval-available">
              Available project credits: <strong>{creditAmount(available)}</strong>
            </p>
            <p className="credits-line" data-testid="ai-approval-required">
              Needed to start: <strong>{creditAmount(required)}</strong>, the most this operation can cost
            </p>
            {range}
            <p className="credits-line">
              Nothing has been reserved or charged, and this estimate is kept while credits are added.
            </p>
            {canPurchase ? null : (
              <p className="credits-line" data-testid="ai-approval-ask-producer">
                A producer can add credits to this production under Plan &amp; billing.
              </p>
            )}
          </Callout>
        </div>
        {buying && canPurchase ? (
          <CreditPackagePicker
            projectId={projectId}
            testId="ai-approval-picker"
            onClose={() => {
              setBuying(false);
            }}
            onForbidden={() => {
              setPurchaseRefused(true);
              setBuying(false);
            }}
            onPaid={() => {
              // The quote is still the one approved next: only the balance changed.
              approve.reset();
              approveKey.current = null;
              setBuying(false);
              setToppedUp(true);
            }}
          />
        ) : null}
      </>,
      buying ? undefined : (
        <>
          {cancelButton}
          {canPurchase ? (
            <button
              type="button"
              className="btn btn-primary"
              data-testid="ai-approval-buy"
              onClick={() => {
                setBuying(true);
              }}
            >
              Buy credits
            </button>
          ) : null}
        </>
      ),
    );
  }

  if (state === 'expired' || state === 'stale') {
    return frame(
      <Callout
        tone="warn"
        title={state === 'expired' ? 'This estimate has expired' : 'The screenplay changed after this estimate'}
        testId={state === 'expired' ? 'ai-approval-expired' : 'ai-approval-stale'}
      >
        {state === 'expired'
          ? 'An estimate is held for a limited time. Nothing was reserved or charged. A new one is worked out from the same screenplay and scenes.'
          : 'The scenes it covers were changed, so the cost has to be worked out again. Nothing was reserved or charged.'}
      </Callout>,
      <>
        {cancelButton}
        {estimateAgainButton}
      </>,
    );
  }

  if (state === 'failed') {
    const neverArrived = answerNeverArrived(approve.error);
    const lifecycle = lifecycleRefusal(approveCode);
    const forbidden = approve.error instanceof ApiError && approve.error.status === 403 && lifecycle === null;

    return frame(
      <Callout
        tone={lifecycle?.tone ?? (forbidden ? 'info' : 'danger')}
        title={lifecycle?.title ?? (forbidden ? 'Not something your role does' : 'The operation could not be started')}
        testId="ai-approval-failed"
      >
        <span data-code={approveCode ?? ''}>
          {lifecycle?.body ??
            (forbidden
              ? (runRefusal ?? 'Your role on this production does not run AI.')
              : neverArrived
                ? 'The answer did not arrive. Trying again repeats the same approval, so nothing can be reserved twice.'
                : approveCode === 'OPERATION_STATE_CONFLICT'
                  ? 'This estimate can no longer be started; it was closed, perhaps in another tab. Nothing was reserved or charged by this press.'
                  : approve.error instanceof ApiError
                    ? approve.error.message
                    : 'Something went wrong.')}
        </span>
      </Callout>,
      <>
        {cancelButton}
        {neverArrived ? (
          <button
            type="button"
            className="btn btn-primary"
            data-testid="ai-approval-retry"
            onClick={() => {
              start(true);
            }}
          >
            Try again
          </button>
        ) : lifecycle === null && !forbidden ? (
          estimateAgainButton
        ) : null}
      </>,
    );
  }

  // ready, or starting
  const available = toppedUp && credits.data !== undefined ? credits.data.availableCents : quote.availableCents;

  return frame(
    <>
      {toppedUp ? (
        <p className="credits-line credits-ok" data-testid="ai-approval-topped-up">
          Credits added. The estimate is unchanged; start it when you are ready.
        </p>
      ) : null}
      <div className="credits-figures">
        {range}
        <p className="credits-line" data-testid="ai-approval-available">
          Available project credits: <strong>{creditAmount(available)}</strong>
        </p>
        <p className="credits-line" data-testid="ai-approval-cap">
          Maximum authorised cost: <strong>{creditAmount(quote.capCents)}</strong>
        </p>
        {toppedUp ? null : (
          <p className="credits-line mm" data-testid="ai-approval-remaining">
            Left after the maximum: {creditAmount(quote.remainingAfterMaxCents)}
          </p>
        )}
      </div>
      <p className="mm credits-note" data-testid="ai-approval-copy">
        Final cost depends on actual usage. You’ll only be charged for what you use.
      </p>
      <p className="mm credits-note" data-testid="ai-approval-reserve-note">
        Starting sets the maximum aside from the production’s credits; whatever is not used goes back.
      </p>
      {state === 'starting' ? (
        <p className="sr-only" role="status" aria-live="polite" data-testid="ai-approval-starting">
          Starting…
        </p>
      ) : null}
    </>,
    <>
      {cancelButton}
      <button
        type="button"
        className="btn btn-primary"
        data-testid="ai-approval-start"
        disabled={state === 'starting' || cancel.isPending}
        onClick={() => {
          start(false);
        }}
      >
        {state === 'starting' ? 'Starting…' : 'Start'}
      </button>
    </>,
  );
}

type Tone = 'ok' | 'warn' | 'info' | 'danger';

/** A production that cannot run AI because of where it stands, not because of the request. */
function lifecycleRefusal(code: string | null): { tone: Tone; title: string; body: string } | null {
  if (code === 'PRODUCTION_READ_ONLY') {
    return {
      tone: 'warn',
      title: 'This production is read-only',
      body: 'Renew its plan under Plan & billing to run AI again. Nothing on it is lost.',
    };
  }
  if (code === 'PROJECT_ARCHIVED') {
    return { tone: 'info', title: 'This production is archived', body: 'AI cannot be run on an archived production.' };
  }

  return null;
}

/** Why the cost could not be worked out, and whether asking again could help. */
function estimateRefusal(
  error: unknown,
  runRefusal: string | null,
): { tone: Tone; title: string; body: string; retry: boolean } {
  const code = refusalCode(error);
  const lifecycle = lifecycleRefusal(code);
  if (lifecycle !== null) return { ...lifecycle, retry: false };

  if (code === 'PRICING_UNAVAILABLE') {
    return {
      tone: 'warn',
      title: 'The cost cannot be worked out right now',
      body: 'Nothing has been reserved or charged. Try again in a few minutes.',
      retry: true,
    };
  }

  if (error instanceof ApiError && error.status === 403) {
    return {
      tone: 'info',
      title: 'Not something your role does',
      body: runRefusal ?? 'Your role on this production does not run AI.',
      retry: false,
    };
  }

  return {
    tone: 'danger',
    title: 'Could not work out the cost',
    body: 'Nothing has been reserved or charged. This is a problem reaching the server, not with your production.',
    retry: true,
  };
}
